import { CartUpdateEvent, ThemeEvents } from '@theme/events';
import { getParentQuantity, getQuantityUpdates, hasCandyRackDiscount } from '@theme/candyrack-upsell-guard-logic';

const CONFIG_SELECTOR = '[data-bosp-candyrack-guard-config]';
const PARENT_PRODUCTS_SELECTOR = '[data-bosp-candyrack-parent-products]';
const CART_UPDATE_SOURCE = 'candyrack-upsell-guard';
const CANDYRACK_REFRESH_EVENT = 'CANDYRACK_SLIDE_CART_UPDATE';
const CANDYRACK_UPDATED_EVENT = 'CANDYRACK_SLIDE_CART_UPDATED';
const CANDYRACK_PDP_ADD_EVENT = 'ppp-candyrack-button-layout-offers-added-to-cart';
const COLLECTION_PAGE_SIZE = 250;
const RECHECK_DELAYS = [150, 700, 1600];

let reconcileInFlight = false;
let reconcileQueued = false;
let missingDiscountPrefixWarningShown = false;
/** @type {Promise<Set<number>> | undefined} */
let parentProductIdsPromise;
/** @type {Map<number, number>} */
const recheckTimers = new Map();

/**
 * @typedef {Object} CartDiscountAllocation
 * @property {number} [amount]
 * @property {{ title?: string }} [discount_application]
 * @property {string} [title]
 *
 * @typedef {Object} CartItem
 * @property {string} key
 * @property {number} product_id
 * @property {number} quantity
 * @property {string} [handle]
 * @property {CartDiscountAllocation[]} [discounts]
 * @property {CartDiscountAllocation[]} [line_level_discount_allocations]
 *
 * @typedef {Object} Cart
 * @property {CartItem[]} items
 * @property {number} item_count
 */

/**
 * @param {unknown} value
 * @returns {value is Record<string, unknown>}
 */
function isRecord(value) {
  return typeof value === 'object' && value !== null;
}

/**
 * @param {unknown} value
 * @returns {value is Cart}
 */
function isCart(value) {
  if (!isRecord(value) || !Array.isArray(value.items) || typeof value.item_count !== 'number') return false;

  return value.items.every(
    (item) =>
      isRecord(item) &&
      typeof item.key === 'string' &&
      typeof item.product_id === 'number' &&
      typeof item.quantity === 'number',
  );
}

/**
 * @param {unknown} value
 * @returns {Cart}
 */
function parseCart(value) {
  if (!isCart(value)) throw new TypeError('Cart response has an unexpected shape.');
  return value;
}

/**
 * @param {CartItem} item
 * @returns {boolean}
 */
function hasPositiveDiscount(item) {
  const allocations = Array.isArray(item.line_level_discount_allocations) ? item.line_level_discount_allocations : [];
  const discounts = Array.isArray(item.discounts) ? item.discounts : [];
  return [...allocations, ...discounts].some((allocation) => Number(allocation.amount || 0) > 0);
}

/** @returns {string} */
function getShopRoot() {
  return window.Shopify.routes?.root || '/';
}

function getConfig() {
  const source = document.querySelector(CONFIG_SELECTOR);
  if (!(source instanceof HTMLScriptElement)) return null;

  try {
    /** @type {unknown} */
    const config = JSON.parse(source.textContent || '{}');
    if (!isRecord(config)) return null;

    const configuredHandles = Array.isArray(config.parentCollectionHandles)
      ? config.parentCollectionHandles
      : typeof config.parentCollectionHandle === 'string'
        ? [config.parentCollectionHandle]
        : [];
    const parentCollectionHandles = configuredHandles
      .filter((handle) => typeof handle === 'string')
      .map((handle) => handle.trim())
      .filter(Boolean);
    const configuredCollections = Array.isArray(config.parentCollections) ? config.parentCollections : [];
    const parentCollections = configuredCollections
      .filter(isRecord)
      .map((collection) => ({
        id: typeof collection.id === 'number' || typeof collection.id === 'string' ? String(collection.id).trim() : '',
        handle: typeof collection.handle === 'string' ? collection.handle.trim() : '',
      }))
      .filter((collection) => collection.id && collection.handle);
    const discountTitlePrefix =
      typeof config.discountTitlePrefix === 'string' ? config.discountTitlePrefix.trim() : '';

    if (parentCollections.length === 0 && parentCollectionHandles.length === 0) {
      return null;
    }
    if (!discountTitlePrefix) {
      return null;
    }

    return {
      parentCollections: parentCollections.filter(
        (collection, index, collections) => collections.findIndex(({ id }) => id === collection.id) === index,
      ),
      parentCollectionHandles: [...new Set(parentCollectionHandles)],
      discountTitlePrefix,
    };
  } catch (error) {
    console.error('Unable to read the Candy Rack upsell guard configuration.', error);
    return null;
  }
}

/**
 * Load collection membership through Shopify's section rendering endpoint.
 * The collection route resolves translated handles and applies the shopper's
 * active market without requiring the collection on the Headless channel.
 *
 * @param {{ id: string, handle: string }} collection
 * @returns {Promise<Set<number>>}
 */
async function loadCollectionProductIdsFromSection(collection) {
  const productIds = new Set();
  let page = 1;

  for (;;) {
    const collectionUrl = new URL(
      `collections/${encodeURIComponent(collection.handle)}`,
      `${window.location.origin}${getShopRoot()}`,
    );
    collectionUrl.searchParams.set('section_id', 'candyrack-parent-products');
    collectionUrl.searchParams.set('page', String(page));

    const response = await fetch(collectionUrl, {
      credentials: 'same-origin',
      headers: { Accept: 'text/html' },
    });

    if (!response.ok) {
      throw new Error(`Parent collection section request failed with status ${response.status}`);
    }

    const sectionDocument = new DOMParser().parseFromString(await response.text(), 'text/html');
    const source = sectionDocument.querySelector(PARENT_PRODUCTS_SELECTOR);
    if (!(source instanceof HTMLScriptElement)) {
      throw new TypeError('Parent collection section response has an unexpected shape.');
    }

    /** @type {unknown} */
    const result = JSON.parse(source.textContent || '{}');
    if (!isRecord(result) || String(result.collectionId || '') !== collection.id || !Array.isArray(result.productIds)) {
      throw new TypeError('Parent collection section data has an unexpected shape.');
    }

    for (const id of result.productIds) {
      const productId = Number(id);
      if (Number.isInteger(productId) && productId > 0) productIds.add(productId);
    }

    if (result.hasNextPage !== true) break;
    page += 1;
  }

  return productIds;
}

/**
 * @param {string} collectionHandle
 * @returns {Promise<Set<number>>}
 */
async function loadCollectionProductIds(collectionHandle) {
  const productIds = new Set();

  for (let page = 1; ; page += 1) {
    const productCountBeforeRequest = productIds.size;
    const collectionUrl = new URL(
      `collections/${encodeURIComponent(collectionHandle)}/products.json`,
      `${window.location.origin}${getShopRoot()}`,
    );
    collectionUrl.searchParams.set('limit', String(COLLECTION_PAGE_SIZE));
    collectionUrl.searchParams.set('page', String(page));

    const response = await fetch(collectionUrl, {
      credentials: 'same-origin',
      headers: { Accept: 'application/json' },
    });

    if (!response.ok) {
      throw new Error(`Parent collection request failed with status ${response.status}`);
    }

    /** @type {unknown} */
    const result = await response.json();
    if (!isRecord(result) || !Array.isArray(result.products)) {
      throw new TypeError('Parent collection response has an unexpected shape.');
    }

    for (const product of result.products) {
      if (!isRecord(product)) continue;

      const productId = Number(product.id);
      if (Number.isInteger(productId) && productId > 0) productIds.add(productId);
    }

    if (result.products.length < COLLECTION_PAGE_SIZE || productIds.size === productCountBeforeRequest) break;
  }

  return productIds;
}

/**
 * Load every product in the merchant-selected parent collections. Collections
 * are loaded concurrently and the combined result is cached for this page.
 *
 * @param {{ parentCollections: { id: string, handle: string }[], parentCollectionHandles: string[] }} config
 * @returns {Promise<Set<number>>}
 */
async function loadParentProductIds(config) {
  let collectionProductIds;

  if (config.parentCollections.length > 0) {
    collectionProductIds = await Promise.all(
      config.parentCollections.map((collection) => loadCollectionProductIdsFromSection(collection)),
    );
  } else {
    collectionProductIds = await Promise.all(config.parentCollectionHandles.map(loadCollectionProductIds));
  }
  const productIds = new Set();

  for (const ids of collectionProductIds) {
    for (const productId of ids) productIds.add(productId);
  }

  return productIds;
}

/** Clear limiter state left on UI rendered before this rule changed. */
function clearQuantityLimitUi() {
  for (const element of document.querySelectorAll('.bosp-cross-sell__offer--at-limit')) {
    element.classList.remove('bosp-cross-sell__offer--at-limit');
  }

  for (const element of document.querySelectorAll('.bosp-candyrack-line--locked')) {
    element.classList.remove('bosp-candyrack-line--locked');
  }
}

/** @returns {Promise<Cart>} */
async function getCart() {
  const response = await fetch(`${getShopRoot()}cart.js`, {
    credentials: 'same-origin',
    headers: { Accept: 'application/json' },
  });

  if (!response.ok) throw new Error(`Cart request failed with status ${response.status}`);
  return parseCart(await response.json());
}

/**
 * @param {Record<string, number>} updates
 * @returns {Promise<Cart>}
 */
async function updateCart(updates) {
  const response = await fetch(`${getShopRoot()}cart/update.js?sliderCartOpen=false&internalCall=true`, {
    method: 'POST',
    credentials: 'same-origin',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ updates }),
  });

  if (!response.ok) throw new Error(`Cart update failed with status ${response.status}`);
  return parseCart(await response.json());
}

/**
 * Refresh both CandyRack's slide cart and Horizon's native cart UI after the
 * guard removes accessories that no longer have a qualifying parent.
 *
 * @param {Cart} cart
 */
function publishCartUpdate(cart) {
  document.dispatchEvent(new CustomEvent(CANDYRACK_REFRESH_EVENT, { detail: { source: CART_UPDATE_SOURCE } }));
  document.dispatchEvent(
    new CartUpdateEvent(cart, CART_UPDATE_SOURCE, {
      itemCount: Number(cart.item_count || 0),
      source: CART_UPDATE_SOURCE,
    }),
  );
}

async function reconcileCart() {
  const config = getConfig();
  if (!config) return;

  if (reconcileInFlight) {
    reconcileQueued = true;
    return;
  }

  reconcileInFlight = true;

  try {
    do {
      reconcileQueued = false;
      let cart = await getCart();
      let parentProductIds;
      let parentCollectionsLoadedDuringReconciliation = false;
      let hasMatchingCandyRackDiscount = cart.items.some((item) =>
        hasCandyRackDiscount(item, config.discountTitlePrefix),
      );

      if (!hasMatchingCandyRackDiscount) {
        const hasUnmatchedPositiveDiscount = cart.items.some(hasPositiveDiscount);

        if (!hasUnmatchedPositiveDiscount) {
          missingDiscountPrefixWarningShown = false;
          clearQuantityLimitUi();
          continue;
        }

        parentCollectionsLoadedDuringReconciliation = !parentProductIdsPromise;
        if (!parentProductIdsPromise) {
          parentProductIdsPromise = loadParentProductIds(config).catch((error) => {
            parentProductIdsPromise = undefined;
            throw error;
          });
        }

        parentProductIds = await parentProductIdsPromise;
        if (parentCollectionsLoadedDuringReconciliation) cart = await getCart();

        hasMatchingCandyRackDiscount = cart.items.some((item) =>
          hasCandyRackDiscount(item, config.discountTitlePrefix),
        );

        if (!hasMatchingCandyRackDiscount) {
          const parentQuantity = getParentQuantity(cart, parentProductIds);
          const hasDiscountedAccessory = cart.items.some(
            (item) => !parentProductIds.has(Number(item.product_id)) && hasPositiveDiscount(item),
          );

          if (parentQuantity > 0 && hasDiscountedAccessory && !missingDiscountPrefixWarningShown) {
            console.warn(
              `Candy Rack upsell guard found a discounted accessory, but no discount title matched the configured prefix "${config.discountTitlePrefix}".`,
            );
            missingDiscountPrefixWarningShown = true;
          }

          clearQuantityLimitUi();
          continue;
        }
      }

      missingDiscountPrefixWarningShown = false;

      if (!parentProductIds) {
        parentCollectionsLoadedDuringReconciliation = !parentProductIdsPromise;
        if (!parentProductIdsPromise) {
          parentProductIdsPromise = loadParentProductIds(config).catch((error) => {
            parentProductIdsPromise = undefined;
            throw error;
          });
        }

        parentProductIds = await parentProductIdsPromise;
        if (parentCollectionsLoadedDuringReconciliation) {
          // Initial collection loading can take long enough for the customer to
          // change the cart. Reconcile that first pass against a fresh snapshot.
          cart = await getCart();

          if (!cart.items.some((item) => hasCandyRackDiscount(item, config.discountTitlePrefix))) {
            clearQuantityLimitUi();
            continue;
          }
        }
      }

      const updates = getQuantityUpdates(cart, parentProductIds, config.discountTitlePrefix);

      if (Object.keys(updates).length > 0) {
        const updatedCart = await updateCart(updates);
        clearQuantityLimitUi();
        publishCartUpdate(updatedCart);
      } else {
        clearQuantityLimitUi();
      }
    } while (reconcileQueued);
  } catch (error) {
    console.error('Unable to enforce the Candy Rack upsell parent requirement.', error);
  } finally {
    reconcileInFlight = false;
  }
}

function scheduleRechecks() {
  for (const delay of RECHECK_DELAYS) {
    window.clearTimeout(recheckTimers.get(delay));
    const timer = window.setTimeout(() => {
      recheckTimers.delete(delay);
      reconcileCart();
    }, delay);
    recheckTimers.set(delay, timer);
  }
}

document.addEventListener(ThemeEvents.cartUpdate, (event) => {
  const detail = event instanceof CustomEvent && isRecord(event.detail) ? event.detail : null;
  const data = detail && isRecord(detail.data) ? detail.data : null;
  if (data?.source !== CART_UPDATE_SOURCE) scheduleRechecks();
});

document.addEventListener(CANDYRACK_PDP_ADD_EVENT, scheduleRechecks);
document.addEventListener(CANDYRACK_REFRESH_EVENT, (event) => {
  const detail = event instanceof CustomEvent && isRecord(event.detail) ? event.detail : null;
  if (detail?.source !== CART_UPDATE_SOURCE) scheduleRechecks();
});
document.addEventListener(CANDYRACK_UPDATED_EVENT, scheduleRechecks);

document.addEventListener('click', (event) => {
  if (!(event.target instanceof Element)) return;
  if (event.target.closest('[class*="candyrack"] button, #candyrack-slider-cart-wrapper button')) scheduleRechecks();
});

window.addEventListener('pageshow', scheduleRechecks);
scheduleRechecks();

window.BospCandyRackUpsellGuard = { reconcile: reconcileCart };
